import { RefObject, useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  COPY_ALL_EVENT,
  COPY_ALL_WITH_CITATIONS_EVENT,
} from '@/components/TextEditor/plugins/CopyAllPlugin/CopyAllPlugin';
import { CONTENT_EDITABLE_ID } from '@/components/TextEditor/TextEditor.utils';
import { useReportError } from '@/hooks/useReportError';

interface UseCopyRenderedContentOptions {
  /**
   * Also serialize citation chips as inline [citations=[...]] tokens in the
   * plain-text clipboard flavor, so pasting into a Luminary editor recreates
   * them as citation chips. See CopyAllPlugin's CopyAllCommandPayload.
   */
  withCitationTokens?: boolean;
}

/**
 * Returns a copy handler that dispatches a copy-all event on the Lexical
 * editor inside the given container ref. The CopyAllPlugin (already mounted
 * in every read-only TextEditor) writes both HTML and plain-text
 * representations to the clipboard, so the pasted result preserves
 * formatting and links without raw markdown syntax.
 */
export function useCopyRenderedContent(
  containerRef: RefObject<HTMLDivElement | null>,
  { withCitationTokens = false }: UseCopyRenderedContentOptions = {}
) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  return useCallback(() => {
    const editorElement = containerRef.current?.querySelector(
      `#${CONTENT_EDITABLE_ID}`
    );

    if (editorElement) {
      editorElement.dispatchEvent(
        new Event(
          withCitationTokens ? COPY_ALL_WITH_CITATIONS_EVENT : COPY_ALL_EVENT
        )
      );
    } else {
      reportError(
        'Failed to copy chat response',
        new Error('Could not find editor element in chat message')
      );
      showFeedback('Failed to copy');
    }
  }, [containerRef, reportError, showFeedback, withCitationTokens]);
}
